import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  Category,
  CategoryCreate,
  CategoryUpdate,
  Subcategory,
  SubcategoryCreate,
  SubcategoryUpdate,
  Expense,
  ExpenseCreate,
  ExpenseUpdate,
  ExpenseFilter,
  Project,
  Attachment,
  CategoryReport,
  TimeReport,
  ReportFilter,
  ExportRequest,
  UserProfile,
  ProfileUpdateData,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string,
    public details?: any,
    public traceId?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Create axios instance with default config
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store for impersonation company ID (set by AdminContext)
let impersonateCompanyId: string | null = null;

export const setImpersonateCompanyId = (companyId: string | null) => {
  impersonateCompanyId = companyId;
};

// Request interceptor to add auth token and impersonation header
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // No valid token available — session likely expired
        console.warn('No auth token available. Session may have expired.');
      }

      // Add impersonation header if SuperAdmin has selected a company
      if (impersonateCompanyId) {
        config.headers['X-Impersonate-Company'] = impersonateCompanyId;
      }
    } catch (error) {
      // fetchAuthSession failed — token expired and refresh failed
      console.warn('Failed to get auth session:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const statusCode = error.response.status;
      const data = error.response.data as any;
      const detail = data?.detail || '';

      // Detect expired/invalid session — force redirect to login
      if (statusCode === 401 || (statusCode === 403 && detail === 'Not authenticated')) {
        // Clear local state and redirect to login
        window.location.href = '/login';
        throw new ApiError('Sesión expirada. Redirigiendo al login...', statusCode, 'SESSION_EXPIRED');
      }

      // Server responded with error status
      const message = data?.message || detail || 'An error occurred';
      const code = data?.code || `HTTP_${statusCode}`;
      const traceId = data?.traceId || error.response.headers['x-trace-id'];
      
      throw new ApiError(message, statusCode, code, data, traceId);
    } else if (error.request) {
      // Request made but no response received
      throw new ApiError(
        'No response from server. Please check your connection.',
        0,
        'NETWORK_ERROR'
      );
    } else {
      // Error setting up request
      throw new ApiError(
        error.message || 'Failed to make request',
        0,
        'REQUEST_ERROR'
      );
    }
  }
);

// Helper function to handle API calls with consistent error handling
export async function apiCall<T>(
  requestFn: () => Promise<AxiosResponse<T>>
): Promise<T> {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      0,
      'UNKNOWN_ERROR'
    );
  }
}


// Category API
export const categoryApi = {
  async getAll(): Promise<Category[]> {
    return apiCall(() => apiClient.get<Category[]>('/api/v1/categories'));
  },

  async getById(categoryId: string): Promise<Category> {
    return apiCall(() => apiClient.get<Category>(`/api/v1/categories/${categoryId}`));
  },

  async create(data: CategoryCreate): Promise<Category> {
    return apiCall(() => apiClient.post<Category>('/api/v1/categories', data));
  },

  async update(categoryId: string, data: CategoryUpdate): Promise<Category> {
    return apiCall(() => apiClient.put<Category>(`/api/v1/categories/${categoryId}`, data));
  },

  async delete(categoryId: string): Promise<void> {
    return apiCall(() => apiClient.delete<void>(`/api/v1/categories/${categoryId}`));
  },
};

// Subcategory API
export const subcategoryApi = {
  async getByCategory(categoryId: string): Promise<Subcategory[]> {
    return apiCall(() => apiClient.get<Subcategory[]>(`/api/v1/categories/${categoryId}/subcategories`));
  },

  async getById(subcategoryId: string): Promise<Subcategory> {
    return apiCall(() => apiClient.get<Subcategory>(`/api/v1/subcategories/${subcategoryId}`));
  },

  async create(data: SubcategoryCreate): Promise<Subcategory> {
    return apiCall(() => apiClient.post<Subcategory>('/api/v1/subcategories', data));
  },

  async update(subcategoryId: string, data: SubcategoryUpdate): Promise<Subcategory> {
    return apiCall(() => apiClient.put<Subcategory>(`/api/v1/subcategories/${subcategoryId}`, data));
  },

  async delete(subcategoryId: string): Promise<void> {
    return apiCall(() => apiClient.delete<void>(`/api/v1/subcategories/${subcategoryId}`));
  },
};

// Project API
export const projectApi = {
  async getAll(statusFilter?: 'active' | 'completed' | 'suspended' | 'all'): Promise<Project[]> {
    const params: Record<string, any> = {};
    if (statusFilter) {
      params.status_filter = statusFilter;
    }
    return apiCall(() => apiClient.get<Project[]>('/api/v1/projects', { params }));
  },

  async getById(projectId: string): Promise<Project> {
    return apiCall(() => apiClient.get<Project>(`/api/v1/projects/${projectId}`));
  },

  async create(data: any): Promise<Project> {
    return apiCall(() => apiClient.post<Project>('/api/v1/projects', data));
  },

  async update(projectId: string, data: any): Promise<Project> {
    return apiCall(() => apiClient.put<Project>(`/api/v1/projects/${projectId}`, data));
  },

  async changeStatus(projectId: string, status: 'active' | 'completed' | 'suspended'): Promise<Project> {
    return apiCall(() => apiClient.patch<Project>(`/api/v1/projects/${projectId}/status?status=${status}`));
  },

  async delete(projectId: string): Promise<void> {
    return apiCall(() => apiClient.delete(`/api/v1/projects/${projectId}`));
  },
};

// Expense API
export const expenseApi = {
  async getAll(filters?: ExpenseFilter, pagination?: { page?: number; pageSize?: number; sortBy?: string; sortOrder?: string }): Promise<any> {
    const params: Record<string, any> = {};
    
    // Filtros
    if (filters?.projectId) params.project_id = filters.projectId;
    if (filters?.categoryId) params.category_id = filters.categoryId;
    if (filters?.subcategoryId) params.subcategory_id = filters.subcategoryId;
    if (filters?.startDate) params.start_date = filters.startDate;
    if (filters?.endDate) params.end_date = filters.endDate;
    if (filters?.minAmount) params.min_amount = filters.minAmount;
    if (filters?.maxAmount) params.max_amount = filters.maxAmount;
    if (filters?.provider) params.provider = filters.provider;
    if (filters?.search) params.search = filters.search;
    if (filters?.userId) params.user_id = filters.userId;
    if (filters?.assetTypeId) params.asset_type_id = filters.assetTypeId;
    if (filters?.assetId) params.asset_id = filters.assetId;
    
    // Paginación y ordenamiento
    if (pagination?.page) params.page = pagination.page;
    if (pagination?.pageSize) params.page_size = pagination.pageSize;
    if (pagination?.sortBy) params.sort_by = pagination.sortBy;
    if (pagination?.sortOrder) params.sort_order = pagination.sortOrder;
    
    const response = await apiCall(() => apiClient.get<any>('/api/v1/expenses', { params }));
    
    // Handle both old format (array) and new format (object with items)
    if (Array.isArray(response)) {
      return {
        items: response,
        pagination: { page: 1, pageSize: response.length, total: response.length, totalPages: 1 },
        summary: { totalAmount: 0, count: response.length }
      };
    }
    
    return response;
  },

  async getById(expenseId: string): Promise<Expense> {
    return apiCall(() => apiClient.get<Expense>(`/api/v1/expenses/${expenseId}`));
  },

  async create(data: ExpenseCreate): Promise<Expense> {
    return apiCall(() => apiClient.post<Expense>('/api/v1/expenses', data));
  },

  async update(expenseId: string, data: ExpenseUpdate): Promise<Expense> {
    return apiCall(() => apiClient.put<Expense>(`/api/v1/expenses/${expenseId}`, data));
  },

  async delete(expenseId: string): Promise<void> {
    return apiCall(() => apiClient.delete<void>(`/api/v1/expenses/${expenseId}`));
  },
};

// Attachment API
export const attachmentApi = {
  async upload(file: File, expenseId?: string): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = expenseId 
      ? `/api/v1/attachments?expense_id=${expenseId}`
      : '/api/v1/attachments';
    
    return apiCall(() => 
      apiClient.post<Attachment>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    );
  },

  async getDownloadUrl(attachmentId: string): Promise<Attachment> {
    return apiCall(() => apiClient.get<Attachment>(`/api/v1/attachments/${attachmentId}`));
  },

  async delete(attachmentId: string): Promise<void> {
    return apiCall(() => apiClient.delete<void>(`/api/v1/attachments/${attachmentId}`));
  },
};

// Report API
export const reportApi = {
  async getCategoryReport(filters?: ReportFilter): Promise<CategoryReport> {
    const params: Record<string, any> = {};
    
    if (filters?.projectId) params.project_id = filters.projectId;
    if (filters?.startDate) params.start_date = filters.startDate;
    if (filters?.endDate) params.end_date = filters.endDate;
    if (filters?.categoryIds?.length) {
      params.category_ids = filters.categoryIds;
    }
    
    return apiCall(() => apiClient.get<CategoryReport>('/api/v1/reports/categories', { params }));
  },

  async getTimeReport(periodType: string, filters?: ReportFilter): Promise<TimeReport> {
    // Map frontend period names to backend expected values
    const periodMap: Record<string, string> = {
      biweekly: '2weeks',
    };
    const params: Record<string, any> = {
      period_type: periodMap[periodType] || periodType,
    };
    
    if (filters?.projectId) params.project_id = filters.projectId;
    if (filters?.startDate) params.start_date = filters.startDate;
    if (filters?.endDate) params.end_date = filters.endDate;
    
    return apiCall(() => apiClient.get<TimeReport>('/api/v1/reports/time', { params }));
  },

  async exportReport(request: ExportRequest): Promise<{ downloadUrl: string; filename: string }> {
    const response = await apiCall(() => 
      apiClient.post<{ downloadUrl: string; filename: string }>(`/api/v1/reports/export/${request.reportType}`, {
        format: request.format,
        filters: request.filters,
        includeCharts: request.includeCharts,
      })
    );
    return response;
  },

  async getUserReport(userId: string, startDate?: string, endDate?: string): Promise<any> {
    const params: Record<string, any> = { user_id: userId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return apiCall(() => apiClient.get<any>('/api/v1/reports/user', { params }));
  },
};

// Profile API
export const profileApi = {
  async getProfile(): Promise<UserProfile> {
    return apiCall(() => apiClient.get<UserProfile>('/api/v1/profile'));
  },

  async updateProfile(data: ProfileUpdateData): Promise<UserProfile> {
    return apiCall(() => apiClient.put<UserProfile>('/api/v1/profile', data));
  },

  async uploadAvatar(file: File): Promise<UserProfile> {
    const formData = new FormData();
    formData.append('file', file);
    return apiCall(() =>
      apiClient.post<UserProfile>('/api/v1/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  },
};

